import Vue from 'vue';
import { get, getBlob, post } from './axiosHelper';
import challengerEnv from '../lib/challengerEnv.js';
import { getFullFormattedDate } from './dateFormatter';
import {
  ICorrespondenceData,
  isCorrespondenceData,
  isCorrespondenceListItem,
  isCorrespondenceDataItem,
} from './Icorrespondence';
import {
  ICorrespondenceTableHeader,
  ICorrespondenceItem,
  ICorrespondence,
} from './ICorrespondenceViewModels';

const correspondencePath = 'external/correspondence-enquiries/v1/api'

const correspondenceHeader = {
  Type: 'type',
  TypeFlag: 'typeFlag',
  AccountNo: 'accountNo',
  ProductName: 'productName',
  Date: 'date',
  Download: 'download',
};

const sortOrderQueryString = {
  account: 'account',
  accountDesc: 'account_desc',
  type: 'type',
  typeDesc: 'type_desc',
  date: 'date',
  dateDesc: 'date_desc',
  productName: 'productName',
  productNameDesc: 'productName_desc',
};

const generateCentrelinkScheduleRequest = async () => {
  const url = `${challengerEnv().apiBaseUrl}/${correspondencePath}/generateDocument`;
  return await post(url);
};

const getTableHeader = () => {
  let headers = new Array<ICorrespondenceTableHeader>();

  // Build the headers
  let typeHeader: ICorrespondenceTableHeader = {
    text: 'Type',
    value: correspondenceHeader.Type,
    sortable: true,
    align: 'start type-col',
  };
  let accountNoHeader: ICorrespondenceTableHeader = {
    text: 'Account number',
    value: correspondenceHeader.AccountNo,
    sortable: true,
    align: 'start accountno-col',
  };
  let productNameHeader: ICorrespondenceTableHeader = {
    text: 'Product',
    value: correspondenceHeader.ProductName,
    sortable: true,
    align: 'start productname-col',
  };
  let dateHeader: ICorrespondenceTableHeader = {
    text: 'Date',
    value: correspondenceHeader.Date,
    sortable: true,
    align: 'start date-col',
  };
  let downloadHeader: ICorrespondenceTableHeader = {
    text: 'Download',
    value: correspondenceHeader.Download,
    sortable: false,
    align: 'start download-col',
  };
  headers.push(dateHeader);
  headers.push(productNameHeader);
  headers.push(accountNoHeader);
  headers.push(typeHeader);
  headers.push(downloadHeader);
  return headers;
};

const getTableHeaderTablet = () => {
  let headers = new Array<ICorrespondenceTableHeader>();

  // Build the headers
  let typeHeader: ICorrespondenceTableHeader = {
    text: 'Type',
    value: correspondenceHeader.Type,
    sortable: true,
    align: 'start type-col',
  };
  let accountNoHeader: ICorrespondenceTableHeader = {
    text: 'Acct no',
    value: correspondenceHeader.AccountNo,
    sortable: true,
    align: 'start accountno-col',
  };
  let productNameHeader: ICorrespondenceTableHeader = {
    text: 'Product',
    value: correspondenceHeader.ProductName,
    sortable: true,
    align: 'start productname-col',
  };
  let dateHeader: ICorrespondenceTableHeader = {
    text: 'Date',
    value: correspondenceHeader.Date,
    sortable: true,
    align: 'start date-col',
  };
  let downloadHeader: ICorrespondenceTableHeader = {
    text: '',
    value: correspondenceHeader.Download,
    sortable: false,
    align: 'start download-col',
  };
  headers.push(dateHeader);
  headers.push(productNameHeader);
  headers.push(accountNoHeader);
  headers.push(typeHeader);
  headers.push(downloadHeader);
  return headers;
};

/* Transform the sort by + desc or asc into the sort order for API  */
const getSortOrder = (sortBy, sortDesc) => {
  let sortOrder = sortOrderQueryString.dateDesc;
  let isSortDescIcon = true;
  if (typeof sortDesc !== 'undefined' && sortDesc.length > 0) {
    isSortDescIcon = sortDesc[0];
  }

  if (isSortDescIcon) {
    if (typeof sortBy != 'undefined' && sortBy.length > 0) {
      switch (sortBy[0]) {
        case correspondenceHeader.AccountNo:
          // IMPORTANT for the Account No column the order is reverse based on Design
          sortOrder = sortOrderQueryString.account;
          break;
        case correspondenceHeader.Type:
          sortOrder = sortOrderQueryString.typeDesc;
          break;
        case correspondenceHeader.ProductName:
          sortOrder = sortOrderQueryString.productNameDesc;
          break;
        case correspondenceHeader.Date:
          // IMPORTANT for the date column the order is reverse based on Design
          sortOrder = sortOrderQueryString.date;
          break;
      }
    }
  } else {
    if (typeof sortBy != 'undefined' && sortBy.length > 0) {
      switch (sortBy[0]) {
        case correspondenceHeader.AccountNo:
          // IMPORTANT for the Account No column the order is reverse based on Design
          sortOrder = sortOrderQueryString.accountDesc;
          break;
        case correspondenceHeader.Type:
          sortOrder = sortOrderQueryString.type;
          break;
        case correspondenceHeader.ProductName:
          sortOrder = sortOrderQueryString.productName;
          break;
        case correspondenceHeader.Date:
          // IMPORTANT for the date column the order is reverse based on Design
          sortOrder = sortOrderQueryString.dateDesc;
          break;
      }
    }
  }
  return sortOrder;
};

/*
Transform the API response into View Models that can be used on the Vue Side
*/
const transformCorrespondenceResponse = (res: ICorrespondenceData): ICorrespondence => {
  // Use User defined Type Guard to ensure the structure of the data matches the Interface
  if (!isCorrespondenceData(res)) {
    if(Vue.prototype.$logger && typeof(Vue.prototype.$logger)!=='undefined'){
      Vue.prototype.$logger.debug('transformCorrespondenceResponse - isCorrespondenceData failed: ', res);
    }
    return null;
  }

  let headers = getTableHeader();
  let headersTablet = getTableHeaderTablet();

  let items = new Array<ICorrespondenceItem>();
  if (res.success) {
    // Check the response data
    if (!isCorrespondenceDataItem(res.data)) {
      if(Vue.prototype.$logger && typeof(Vue.prototype.$logger)!=='undefined'){
        Vue.prototype.$logger.debug('transformCorrespondenceResponse - isCorrespondenceDataItem failed: ', res);
      }
      return null;
    }

    // Loop through ALl policies and build the model
    res.data.list.forEach((element) => {
      // Use User defined Type Guard to ensure the structure of the data matches the Interface
      if (!isCorrespondenceListItem(element)) {
        if(Vue.prototype.$logger && typeof(Vue.prototype.$logger)!=='undefined'){
          Vue.prototype.$logger.debug('transformCorrespondenceResponse - isCorrespondenceListItem failed: ', element);
        }
        return null;
      } else {
        const cItem: ICorrespondenceItem = {
          documentId: element.id,
          type: element.type.description,
          accountNo: element.accountNo,
          productName: element.productName,
          date: getFullFormattedDate(new Date(element.date)),
          download: '',
        };

        if (cItem !== null) {
          items.push(cItem);
        }
      }
    });

    const correspondences: ICorrespondence = {
      headers: headers,
      headersTablet: headersTablet,
      items: items,
      totalCount: res.data.totalCount,
      pageNumber: res.data.pageNumber,
      pageSize: res.data.pageSize,
      totalPages: res.data.totalPages,
    };

    return correspondences;
  } else {
    //TODO: Error handler
  }
  return null;
};

/*
Get the Data from API call: Policies data + Maturity Banner
Return the View Models for policies and Maturity
*/
const getCorrespondences = async (pageNumber, pageSize, sortOrder): Promise<ICorrespondence> => {
  let defaultPageNumber = 1;
  let defaultPageSize = 10;
  let defaultSortOrder = sortOrderQueryString.dateDesc;

  if (typeof pageNumber == 'undefined') {
    pageNumber = defaultPageNumber;
  }
  if (typeof pageSize == 'undefined') {
    pageSize = defaultPageSize;
  }
  if (typeof sortOrder == 'undefined') {
    sortOrder = defaultSortOrder;
  }

  if (challengerEnv().mockApiResponses) {
    // MOCKING Sorting as well...
    let correspondences = require('../../cypress/fixtures/correspondences/mock-correspondences_' +
      pageNumber +
      '.json'); // simulate API with Mock data...
    if (sortOrder == sortOrderQueryString.account || sortOrder == sortOrderQueryString.accountDesc) {
      correspondences = require('../../cypress/fixtures/correspondences/Sorted/accountNo_' +
        pageNumber +
        '.json');
    } else if (sortOrder == sortOrderQueryString.date || sortOrder == sortOrderQueryString.dateDesc) {
      correspondences = require('../../cypress/fixtures/correspondences/Sorted/date_' + pageNumber + '.json');
    }
  
    const correspondencesModel = transformCorrespondenceResponse(correspondences);
    return correspondencesModel;
    }
  else{
    const url = `${challengerEnv().apiBaseUrl}/${correspondencePath}/correspondences?pageNumber=${pageNumber}&pageSize=${pageSize}&sortOrder=${sortOrder}`;
    const correspondences: ICorrespondenceData = await get(url); // This needs to be enable for the API interaction
    const correspondencesModel = transformCorrespondenceResponse(correspondences);
    return correspondencesModel;
  }
};

export {
  generateCentrelinkScheduleRequest,
  getCorrespondences,
  transformCorrespondenceResponse,
  getSortOrder,
  correspondenceHeader,
  sortOrderQueryString,
};
